import { HashTable } from './HashTable.js';
import { isDefined } from '../utils/functions.js';
import { Observable } from '../utils/Observable.js';

/**
 * Represents a collection of key-value pairs.
 * Important: do not use the standard Array access method, use the get/Set methods instead.
 */
class Dictionary extends Observable {
    /**
     * Initializes a new instance of the Dictionary class.
     *
     * @param dictionary Loads the content of the given dictionary into this new one.
     */
    constructor(dictionary) {
        super();
        this._hashTable = new HashTable();
        this.length = 0;
        if (isDefined(dictionary)) {
            if (Array.isArray(dictionary)) {
                for (let i = 0; i < dictionary.length; i++) {
                    this.add(dictionary[i]);
                }
            }
            else {
                dictionary.forEach(function (k, v) {
                    this.add(k, v);
                }, this);
            }
        }
    }
    /**
     * Adds a key-value to the dictionary.
     * If the key already exists this will assign the given value to the existing entry.
     */
    add(key, value) {
        let entry = this._hashTable.get(key);
        if (!entry) {
            entry = this._hashTable.add(key);
            this.length++;
            this.trigger('changed');
        }
        entry.value = value;
    }
    /**
     * Set the key-value pair.
     *
     * @param key The key of the entry.
     * @param value The value to set. If the key already exists the value will be overwritten.
     */
    set(key, value) {
        this.add(key, value);
    }
    /**
     * Gets the value associated with the given key in the dictionary.
     */
    get(key) {
        const entry = this._hashTable.get(key);
        if (entry) {
            return entry.value;
        }
        throw new Error('Cannot find key ' + key);
    }
    /**
     * Returns whether the dictionary contains the given key.
     */
    containsKey(key) {
        return this._hashTable.containsKey(key);
    }
    /**
     * Removes the element with the specified key from the dictionary.
     */
    remove(key) {
        if (this.containsKey(key)) {
            this.trigger('changed');
            this.length--;
            return this._hashTable.remove(key);
        }
    }
    /**
     * The functional gets the key and value as parameters.
     */
    forEach(func, thisRef) {
        this._hashTable.forEach(function (entry) {
            func.call(thisRef, entry.key, entry.value);
        });
    }
    /**
     * Same as forEach except that only the value is passed to the functional.
     */
    forEachValue(func, thisRef) {
        this._hashTable.forEach(function (entry) {
            func.call(thisRef, entry.value);
        });
    }
    /**
     * Calls a defined callback function for each key in the dictionary.
     */
    forEachKey(func, thisRef) {
        this._hashTable.forEach(function (entry) {
            func.call(thisRef, entry.key);
        });
    }
    /**
     * Gets an array with all keys in the dictionary.
     */
    keys() {
        const keys = [];
        this.forEachKey(function (key) {
            keys.push(key);
        });
        return keys;
    }
}

export { Dictionary };
